import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBranchContext } from "@/hooks/useBranchContext";
import { useOrganizationContext } from "@/hooks/useOrganizationContext";

export interface TeamMemberStats {
  id: string;
  full_name: string;
  email: string | null;
  phone_number: string;
  branch: string | null;
  status: string | null;
  role: string;
  tasksCompleted: number;
  avgCompletionTime: number | null;
}

export const useTeamStats = () => {
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();
  return useQuery({
    queryKey: ['team-stats', organizationId, currentBranchId],
    queryFn: async () => {
      if (currentBranchId == null || !organizationId) return [];
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select(`id, full_name, email, phone_number, branch, status, profile_branches!inner(branch_id)`)
        .eq('organization_id', organizationId)
        .eq('profile_branches.branch_id', currentBranchId)
        .order('created_at', { ascending: false });
      if (profilesError) throw profilesError;

      const membersWithStats = await Promise.all(
        (profiles || []).map(async (profile) => {
          const { data: roles } = await supabase.from('user_roles').select('role').eq('user_id', profile.id).limit(1);
          const { data: completedTasks } = await supabase.from('activities').select('time_taken_minutes')
            .eq('completed_by', profile.id).eq('status', 'completed')
            .eq('branch_id', currentBranchId).eq('organization_id', organizationId);
          const tasksCompleted = completedTasks?.length || 0;
          const totalTime = completedTasks?.reduce((sum, task) => sum + (task.time_taken_minutes || 0), 0) || 0;
          return {
            id: profile.id, full_name: profile.full_name || 'Unknown', email: profile.email,
            phone_number: profile.phone_number, branch: profile.branch, status: profile.status,
            role: roles?.[0]?.role || 'No Role', tasksCompleted,
            avgCompletionTime: tasksCompleted > 0 ? totalTime / tasksCompleted : null,
          };
        })
      );
      return membersWithStats as TeamMemberStats[];
    },
    enabled: currentBranchId != null && !!organizationId,
  });
};
